import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
  Tabs,
  Tab
} from "@mui/material";
import {
  Edit,
  Delete,
  ArrowForward,
  Close
} from "@mui/icons-material";
import CustomInputField from "../common/CustomInputField";
import { compatibilityService } from "../../services/compatibilityService";
import { systemPromptService } from "../../services/systemPromptService";
import { CHAT_CATEGORY_OPTIONS } from "../../constants/apiConstants";
import { getPageNumbers } from "../../utils/pagination";
const CompatibilityAI = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Compatibilities state
  const [compatibilities, setCompatibilities] = useState([]);
  const [loadingCompatibilities, setLoadingCompatibilities] = useState(true);
  const [, setCompatMessage] = useState("");

  // Compare state (duplicate for Compare tab)
  const [compares, setCompares] = useState([]);
  const [loadingCompares, setLoadingCompares] = useState(true);
  const [, setCompareMessage] = useState("");
  const [comparePage, setComparePage] = useState(1);
  const [compareTotalPages, setCompareTotalPages] = useState(1);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);


  const [modalOpen, setModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    type: "",
    prompt: ""
  });
  const [originalEditForm, setOriginalEditForm] = useState(null);
  const [neutralMessage, setNeutralMessage] = useState("");
  const [compatibilityTypes, setCompatibilityTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Compute the value to pass to Autocomplete so it shows correctly whether
  // options are objects or strings. If a matching option exists, pass that;
  // otherwise pass the raw string so freeSolo still shows the value.

  // Local input state for the Autocomplete text so the typed/selected value
  // stays visible immediately when adding/editing.
  const [typeInput, setTypeInput] = useState(editForm.type || "");

  useEffect(() => {
    // Sync local input when editForm.type changes (e.g., when opening edit modal)
    setTypeInput(editForm.type || "");
  }, [editForm.type, modalOpen]);

  // Compute the matching option object from `compatibilityTypes` if one exists.

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCompatibilityId, setEditingCompatibilityId] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCompatibility, setDeletingCompatibility] = useState(null);

  // API related states
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const fetchCompatibilities = useCallback(async (isComparison = false) => {
    try {
      setLoadingCompatibilities(true);
      const result = await compatibilityService.getCompatibilities(isComparison ? { is_comparison: true } : {});

      const payload = result.data?.result ?? result.data;
      // If payload is an array with items, set them; otherwise clear and set message
      if (result.success && Array.isArray(payload) && payload.length > 0) {
        setCompatibilities(payload);
        setCompatMessage("");
        // Calculate total pages for pagination
        setTotalPages(Math.ceil(payload.length / itemsPerPage));
      } else {
        console.warn("No compatibilities returned:", result.message ?? result.data);
        setCompatibilities([]);
        setTotalPages(0);
        setCompatMessage(result.message || result.data?.detail || (isComparison ? 'No Compare Found' : 'No Compatibilities Found'));
      }
    } catch (error) {
      console.error("Error fetching compatibilities:", error);
      setCompatibilities([]);
      setTotalPages(0);
      setCompatMessage(isComparison ? 'No Compare Found' : 'Failed to fetch compatibilities');
    } finally {
      setLoadingCompatibilities(false);
    }
  }, [itemsPerPage]);

  // Fetch compare data (duplicate same API for now)
  const fetchCompares = useCallback(async (isComparison = false) => {
    try {
      setLoadingCompares(true);
      const result = await compatibilityService.getCompatibilities(isComparison ? { is_comparison: true } : {});

      const payload = result.data?.result ?? result.data;
      if (result.success && Array.isArray(payload) && payload.length > 0) {
        setCompares(payload);
        setCompareMessage("");
        setCompareTotalPages(Math.ceil(payload.length / itemsPerPage));
      } else {
        console.warn("No compare items returned:", result.message ?? result.data);
        setCompares([]);
        setCompareTotalPages(0);
        setCompareMessage(result.message || result.data?.detail || 'No Compare Found');
      }
    } catch (error) {
      console.error("Error fetching compares:", error);
      setCompares([]);
      setCompareTotalPages(0);
      setCompareMessage('Failed to fetch compare items');
    } finally {
      setLoadingCompares(false);
    }
  }, [itemsPerPage]);

  const fetchCompatibilityTypes = useCallback(async () => {
    try {
      setLoadingTypes(true);
      const res = await systemPromptService.getSystemPrompts();

      if (res.success) {
        const payload = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.result)
            ? res.data.result
            : Array.isArray(res.data?.data)
              ? res.data.data
              : [];

        if (payload.length > 0) {
          const categories = Array.from(
            new Set(
              payload
                .map((item) => item.category)
                .filter((c) => c !== undefined && c !== null)
            )
          ).map((value) => ({
            value,
            label: String(value)
              .replace(/_/g, " ")
              .replace(/\w\S*/g, (w) => w.replace(/^(\w)/, (c) => c.toUpperCase())),
          }));

          setCompatibilityTypes(categories);
          return;
        }
      }

      // Fallback: derive unique types from existing compatibilities or use CHAT_CATEGORY_OPTIONS
      const unique = Array.from(new Set(compatibilities.map((c) => c.type).filter(Boolean)));
      if (unique.length > 0) {
        setCompatibilityTypes(unique.map((v) => ({ value: v, label: String(v) })));
      } else {
        setCompatibilityTypes(CHAT_CATEGORY_OPTIONS);
      }
    } catch (err) {
      console.error("Error fetching compatibility types:", err);
      const unique = Array.from(new Set(compatibilities.map((c) => c.type).filter(Boolean)));
      if (unique.length > 0) {
        setCompatibilityTypes(unique.map((v) => ({ value: v, label: String(v) })));
      } else {
        setCompatibilityTypes(CHAT_CATEGORY_OPTIONS);
      }
    } finally {
      setLoadingTypes(false);
    }
  }, [compatibilities]);

  // Fetch compatibilities on component mount and when tab changes
  useEffect(() => {
    const isComparison = activeTab === 1;
    fetchCompatibilities(isComparison);
    fetchCompares(isComparison);
    fetchCompatibilityTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return compatibilities.slice(startIndex, endIndex);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleEditCompatibility = (compatibilityId) => {
    // Find the compatibility to edit from appropriate list based on active tab
    const sourceList = activeTab === 1 ? compares : compatibilities;
    const compatibilityToEdit = sourceList.find(compatibility =>
      (compatibility._id?.$oid || compatibility._id) === compatibilityId
    );

    if (compatibilityToEdit) {
      const newEditForm = {
        type: compatibilityToEdit.type,
        prompt: compatibilityToEdit.prompt
      };
      setEditForm(newEditForm);
      setOriginalEditForm(newEditForm);
      setNeutralMessage("");
      setIsEditMode(true);
      setEditingCompatibilityId(compatibilityId);
      setModalOpen(true);
    }
  };

  const handleOpenModal = () => {
    // Reset to add mode
    setIsEditMode(false);
    setEditingCompatibilityId(null);
    setEditForm({
      type: "",
      prompt: ""
    });
    setOriginalEditForm({ type: "", prompt: "" });
    setNeutralMessage("");
    setModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleModalReset = () => {
    if (isEditMode) {
      // In edit mode, close the modal
      handleCloseModal();
    } else {
      // In add mode, reset the form
      setEditForm({
        type: "",
        prompt: ""
      });
      setError("");
      setSuccess("");
      setFieldErrors({});
    }
  };

  const isPromptChanged = () => {
    const current = (editForm.prompt || "").trim();
    const original = (originalEditForm?.prompt || "").trim();
    if (isEditMode) {
      return current !== original;
    }
    return current !== "";
  };

  const validateModalForm = () => {
    const errors = {};

    // Always validate type (allow editing of type in edit mode)
    if (!editForm.type) {
      errors.type = "Type is required";
    }

    if (!editForm.prompt) {
      errors.prompt = "Prompt is required";
    } else if (editForm.prompt.length < 10) {
      errors.prompt = "Prompt must be at least 10 characters long";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleModalCustomize = async () => {
    setError("");
    setSuccess("");
    setFieldErrors({});

    // If editing and no changes, show neutral message and do not submit
    if (isEditMode && originalEditForm) {
      const current = { type: editForm.type || "", prompt: editForm.prompt || "" };
      const orig = { type: originalEditForm.type || "", prompt: originalEditForm.prompt || "" };
      if (JSON.stringify(current) === JSON.stringify(orig)) {
        setNeutralMessage("No changes to save.");
        setTimeout(() => setNeutralMessage(""), 3000);
        return;
      }
    }

    if (!validateModalForm()) {
      return;
    }

    setLoading(true);

    try {
      let result;

      if (isEditMode && editingCompatibilityId) {
        // Update existing compatibility
        result = await compatibilityService.updateCompatibility(editingCompatibilityId, {
          type: editForm.type,
          prompt: editForm.prompt
        }, { is_comparison: activeTab === 1 });
      } else {
        // Create new compatibility
        result = await compatibilityService.createCompatibility({
          type: editForm.type,
          prompt: editForm.prompt
        }, { is_comparison: activeTab === 1 });
      }

      if (result.success) {
        setSuccess(result.message || (isEditMode ? 'Compatibility updated successfully!' : 'Compatibility created successfully!'));
        // Refresh the compatibilities list
        await fetchCompatibilities(activeTab === 1);
        await fetchCompares(activeTab === 1);
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setError(result.message || `Failed to ${isEditMode ? 'update' : 'create'} compatibility`);
      }
    } catch (error) {
      console.error(`${isEditMode ? 'Update' : 'Create'} compatibility error:`, error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsEditMode(false);
    setEditingCompatibilityId(null);
    setEditForm({
      type: "",
      prompt: ""
    });
    setError("");
    setSuccess("");
    setFieldErrors({});
    setNeutralMessage("");
    setOriginalEditForm(null);
  };

  const handleOpenDeleteDialog = (compatibility) => {
    setDeletingCompatibility(compatibility);
    setDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeletingCompatibility(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCompatibility) return;

    const id = deletingCompatibility._id?.$oid || deletingCompatibility._id;
    setActionLoading(true);
    try {
      const result = await compatibilityService.deleteCompatibility(id, activeTab === 1 ? { is_comparison: true } : {});

      if (result.success) {
        await fetchCompatibilities(activeTab === 1);
        await fetchCompares(activeTab === 1);
        setDeleteDialogOpen(false);
        setDeletingCompatibility(null);
        setSuccess("Compatibility deleted successfully.");
        setTimeout(() => setSuccess(""), 3000);
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error("Error deleting compatibility:", err);
      alert('Failed to delete compatibility. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100%", width: "100%" }}>
      {/* Alerts - Full Width */}
      <Box sx={{ width: "100%", marginBottom: 3 }}>
        {success && (
          <Alert 
            severity="success"
            icon={false}
            onClose={() => setSuccess("")} 
            sx={{ 
              backgroundColor: "#4caf50", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {success}
          </Alert>
        )}
        {error && (
          <Alert 
            severity="error"
            icon={false}
            onClose={() => setError("")} 
            sx={{ 
              backgroundColor: "#f44336", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {error}
          </Alert>
        )}
        {neutralMessage && (
          <Alert 
            severity="info"
            icon={false}
            onClose={() => setNeutralMessage("")} 
            sx={{ 
              backgroundColor: "#2196f3", 
              color: "#FFFFFF",
              borderRadius: 0,
              padding: "16px 32px",
              fontSize: "18px",
              fontWeight: 500,
              border: "none",
              display: "flex",
              alignItems: "center",
              minHeight: "56px",
              "& .MuiAlert-message": {
                color: "#FFFFFF",
                width: "100%",
                fontSize: "18px",
                display: "flex",
                alignItems: "center"
              },
              "& .MuiAlert-action": {
                color: "#FFFFFF",
                paddingTop: 0
              }
            }}
          >
            {neutralMessage}
          </Alert>
        )}
      </Box>

      {/* Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 4,
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 0 }
      }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              marginBottom: 1,
              fontSize: { xs: "24px", md: "32px" }
            }}
          >
            Manage Compatibility & Compare
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#888888",
              fontSize: "16px"
            }}
          >
            Manage Compatibility & Compare
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: "#E1AD01",
            color: "#000000",
            borderRadius: "25px",
            padding: "12px 24px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#B8941F"
            }
          }}
        >
          Configure {activeTab === 0 ? 'Compatibility' : 'Compare'} AI
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: '#333333', marginBottom: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{
            '& .MuiTab-root': {
              color: '#888888',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 600,
              '&.Mui-selected': {
                color: '#E1AD01'
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#E1AD01'
            }
          }}
        >
          <Tab label="Compatibility" />
          <Tab label="Compare" />
        </Tabs>
      </Box>

      {/* Compatibility Types Tab */}
      {activeTab === 0 && (
        <Card sx={{
          backgroundColor: "#1A1A1A",
          borderRadius: "16px",
          marginBottom: 4,
          border: "1px solid #333333"
        }}>
          <CardContent sx={{ padding: 4 }}>
            {/* Compatibilities List */}
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                marginBottom: 3,
                fontSize: "18px"
              }}
            >
              Compatibility
            </Typography>

          <Box sx={{ marginBottom: 4 }}>
            {loadingCompatibilities ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                <CircularProgress sx={{ color: "#E1AD01" }} />
              </Box>
            ) : compatibilities.length > 0 ? (
              getCurrentPageItems().map((compatibility) => (
                <Box
                  key={compatibility._id?.$oid || compatibility._id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    backgroundColor: "#2A2A2A",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    marginBottom: 2,
                    border: "1px solid #333333"
                  }}
                >
                  <Box sx={{ flex: 1, paddingRight: 2 }}>
                    <Typography sx={{
                      color: "#E1AD01",
                      fontSize: "16px",
                      fontWeight: 600,
                      marginBottom: 1,
                      textTransform: "capitalize"
                    }}>
                      {compatibility.type}
                    </Typography>
                    <Typography sx={{
                      color: "#CCCCCC",
                      fontSize: "14px",
                      lineHeight: 1.6,
                      display: '-webkit-box',
                      WebkitLineClamp: expandedPrompts[compatibility._id?.$oid || compatibility._id] ? 'unset' : 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-all'
                    }}>
                      {compatibility.prompt}
                    </Typography>
                    {compatibility.prompt?.length > 150 && (
                      <Typography
                        component="span"
                        onClick={() => setExpandedPrompts(prev => ({ ...prev, [compatibility._id?.$oid || compatibility._id]: !prev[compatibility._id?.$oid || compatibility._id] }))}
                        sx={{
                          color: "#E1AD01",
                          cursor: "pointer",
                          marginTop: 0.5,
                          display: 'inline-block',
                          fontWeight: 600,
                          fontSize: "14px",
                          textDecoration: 'none',
                          '&:hover': { 
                            textDecoration: 'underline',
                            color: '#B8941F'
                          }
                        }}
                      >
                        {expandedPrompts[compatibility._id?.$oid || compatibility._id] ? 'View Less' : 'View More'}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => handleEditCompatibility(compatibility._id?.$oid || compatibility._id)}
                      sx={{
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: "#B8941F"
                        }
                      }}
                    >
                      <Edit sx={{ fontSize: 18 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => handleOpenDeleteDialog(compatibility)}
                      sx={{
                        backgroundColor: "#F44336",
                        color: "#FFFFFF",
                        width: 36,
                        height: 36,
                        "&:hover": {
                          backgroundColor: "#D32F2F"
                        }
                      }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Box sx={{
                textAlign: "center",
                padding: 4,
                color: "#888888"
              }}>
                <Typography>No compatibilities found. Add your first compatibility above.</Typography>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          {compatibilities.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  borderColor: "#666666",
                  color: "#CCCCCC",
                  borderRadius: "20px",
                  padding: "8px 20px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#E1AD01",
                    backgroundColor: "rgba(212, 175, 55, 0.1)"
                  },
                  "&:disabled": {
                    borderColor: "#333333",
                    color: "#666666"
                  }
                }}
              >
                Previous
              </Button>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                  page === "..." ? (
                    <Box key={`ellipsis-${idx}`} sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}>...</Box>
                  ) : (
                    <Box
                      key={page}
                      onClick={() => handlePageChange(page)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: page === currentPage ? "#E1AD01" : "#333333",
                        color: page === currentPage ? "#000000" : "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: page === currentPage ? "#B8941F" : "#444444"
                        }
                      }}
                    >
                      {page}
                    </Box>
                  )
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  backgroundColor: "#E1AD01",
                  color: "#000000",
                  borderRadius: "20px",
                  padding: "8px 20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#B8941F"
                  },
                  "&:disabled": {
                    backgroundColor: "#333333",
                    color: "#666666"
                  }
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      )}

      {/* Compare Tab */}
      {activeTab === 1 && (
        <Card sx={{
          backgroundColor: "#1A1A1A",
          borderRadius: "16px",
          marginBottom: 4,
          border: "1px solid #333333"
        }}>
          <CardContent sx={{ padding: 4 }}>
            {/* Compare List */}
            <Typography
              variant="h6"
              sx={{
                color: "#FFFFFF",
                fontWeight: 600,
                marginBottom: 3,
                fontSize: "18px"
              }}
            >
              Compare
            </Typography>

            <Box sx={{ marginBottom: 4 }}>
              {loadingCompares ? (
                <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                  <CircularProgress sx={{ color: "#E1AD01" }} />
                </Box>
              ) : compares.length > 0 ? (
                compares.slice((comparePage - 1) * itemsPerPage, comparePage * itemsPerPage).map((compare) => (
                  <Box
                    key={compare._id?.$oid || compare._id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      backgroundColor: "#2A2A2A",
                      borderRadius: "12px",
                      padding: "16px 20px",
                      marginBottom: 2,
                      border: "1px solid #333333"
                    }}
                  >
                    <Box sx={{ flex: 1, paddingRight: 2 }}>
                      <Typography sx={{
                        color: "#E1AD01",
                        fontSize: "16px",
                        fontWeight: 600,
                        marginBottom: 1,
                        textTransform: "capitalize"
                      }}>
                        {compare.type}
                      </Typography>
                      <Typography sx={{
                        color: "#CCCCCC",
                        fontSize: "14px",
                        lineHeight: 1.6,
                        display: '-webkit-box',
                        WebkitLineClamp: expandedPrompts[compare._id?.$oid || compare._id] ? 'unset' : 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-all'
                      }}>
                        {compare.prompt}
                      </Typography>
                      {compare.prompt?.length > 150 && (
                        <Typography
                          component="span"
                          onClick={() => setExpandedPrompts(prev => ({ ...prev, [compare._id?.$oid || compare._id]: !prev[compare._id?.$oid || compare._id] }))}
                          sx={{
                            color: "#E1AD01",
                            cursor: "pointer",
                            marginTop: 0.5,
                            display: 'inline-block',
                            fontWeight: 600,
                            fontSize: "14px",
                            textDecoration: 'none',
                            '&:hover': { 
                              textDecoration: 'underline',
                              color: '#B8941F'
                            }
                          }}
                        >
                          {expandedPrompts[compare._id?.$oid || compare._id] ? 'View Less' : 'View More'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        onClick={() => handleEditCompatibility(compare._id?.$oid || compare._id)}
                        sx={{
                          backgroundColor: "#E1AD01",
                          color: "#000000",
                          width: 36,
                          height: 36,
                          "&:hover": {
                            backgroundColor: "#B8941F"
                          }
                        }}
                      >
                        <Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteDialog(compare)}
                        sx={{
                          backgroundColor: "#F44336",
                          color: "#FFFFFF",
                          width: 36,
                          height: 36,
                          "&:hover": {
                            backgroundColor: "#D32F2F"
                          }
                        }}
                      >
                        <Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box sx={{
                  textAlign: "center",
                  padding: 4,
                  color: "#888888"
                }}>
                  <Typography>No Compare Found</Typography>
                </Box>
              )}
            </Box>

            {/* Pagination for Compare */}
            {compares.length > itemsPerPage && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setComparePage(prev => Math.max(1, prev - 1))}
                  disabled={comparePage === 1}
                  sx={{
                    borderColor: "#666666",
                    color: "#CCCCCC",
                    borderRadius: "20px",
                    padding: "8px 20px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#E1AD01",
                      backgroundColor: "rgba(212, 175, 55, 0.1)"
                    },
                    "&:disabled": {
                      borderColor: "#333333",
                      color: "#666666"
                    }
                  }}
                >
                  Previous
                </Button>

                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  {getPageNumbers(comparePage, compareTotalPages).map((page, idx) =>
                    page === "..." ? (
                      <Box key={`ellipsis-${idx}`} sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}>...</Box>
                    ) : (
                      <Box
                        key={page}
                        onClick={() => setComparePage(page)}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          backgroundColor: page === comparePage ? "#E1AD01" : "#333333",
                          color: page === comparePage ? "#000000" : "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: page === comparePage ? "#B8941F" : "#444444"
                          }
                        }}
                      >
                        {page}
                      </Box>
                    )
                  )}
                </Box>

                <Button
                  variant="contained"
                  onClick={() => setComparePage(prev => Math.min(compareTotalPages, prev + 1))}
                  disabled={comparePage === compareTotalPages}
                  sx={{
                    backgroundColor: "#E1AD01",
                    color: "#000000",
                    borderRadius: "20px",
                    padding: "8px 20px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#B8941F"
                    },
                    "&:disabled": {
                      backgroundColor: "#333333",
                      color: "#666666"
                    }
                  }}
                >
                  Next
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit/Add Compatibility Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "8px"
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 32px 16px 32px",
        }}>
          <Typography sx={{
            color: "#333333",
            fontSize: "24px",
            fontWeight: 600
          }}>
            {isEditMode ? `Edit ${activeTab === 0 ? 'Compatibility' : 'Compare'}` : `Add ${activeTab === 0 ? 'Compatibility' : 'Compare'}`}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: "#666666",
              "&:hover": { backgroundColor: "#F0F0F0" }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 32px 32px 32px", }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                marginBottom: 2,
                backgroundColor: "#ffebee",
                color: "#c62828",
                whiteSpace: "pre-line"
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              sx={{
                marginBottom: 2,
                backgroundColor: "#e8f5e8",
                color: "#2e7d32"
              }}
            >
              {success}
            </Alert>
          )}

          {/* Neutral Alert for unchanged saves */}
          {neutralMessage && (
            <Alert
              severity="info"
              sx={{
                marginBottom: 2,
                backgroundColor: "#f1f8ff",
                color: "#155fa0"
              }}
            >
              {neutralMessage}
            </Alert>
          )}

          <Box sx={{ marginBottom: 3, marginTop: 2 }}>
            <Box sx={{ position: 'relative' }}>
              <Typography
                sx={{
                  position: 'absolute',
                  top: '-9px',
                  left: '14px',
                  backgroundColor: 'rgba(204, 158, 73, 1)',
                  color: 'black',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  zIndex: 10
                }}
              >
                {activeTab === 0 ? 'Compatibility Type' : 'Compare Type'}
              </Typography>

              <Autocomplete
                freeSolo
                inputValue={typeInput}
                onChange={(event, newValue) => {
                  // newValue may be a string or an object (depending on options)
                  const normalized = typeof newValue === 'string' ? newValue : (newValue?.value || newValue?.label || '');
                  setTypeInput(normalized || '');
                  handleEditFormChange('type', normalized || '');
                  if (error) setError('');
                  if (fieldErrors.type) setFieldErrors(prev => ({ ...prev, type: '' }));
                }}
                onInputChange={(event, newInputValue) => {
                  setTypeInput(newInputValue);
                  handleEditFormChange('type', newInputValue);
                  if (error) setError('');
                  if (fieldErrors.type) setFieldErrors(prev => ({ ...prev, type: '' }));
                }}
                options={compatibilityTypes.map(t => (typeof t === 'string' ? t : (t.value || t.label || '')))}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || option.value || '')}
                renderOption={(props, option) => (
                  <li {...props}>
                    {typeof option === 'string' ? option : option.label || option.value}
                  </li>
                )}
                loading={loadingTypes}
                PopperProps={{ style: { zIndex: 1400 } }}
                ListboxProps={{ sx: { backgroundColor: '#FFFFFF', '& .MuiAutocomplete-option': { color: '#333333' } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={`Select or type ${activeTab === 0 ? 'compatibility type' : 'compare type'}`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        borderRadius: '12px',
                        '& fieldset': { borderColor: '#E1AD01' },
                        '&:hover fieldset': { borderColor: '#E1AD01' },
                        '&.Mui-focused fieldset': { borderColor: '#E1AD01', borderWidth: '2px' }
                      },
                      '& input': { padding: '14px 16px', color: '#000000' }
                    }}
                  />
                )}
              />
            </Box>

            {fieldErrors.type && (
              <Typography sx={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px', marginLeft: '14px' }}>
                {fieldErrors.type}
              </Typography>
            )}
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            <CustomInputField
              label={activeTab === 0 ? "Set Compatibility Prompt" : "Set Compare Prompt"}
              placeholder="Type Here"
              value={editForm.prompt}
              onChange={(e) => {
                handleEditFormChange("prompt", e.target.value);
                if (error) setError("");
                if (fieldErrors.prompt) {
                  setFieldErrors(prev => ({ ...prev, prompt: "" }));
                }
              }}
              disabled={loading}
              multiline={true}
              rows={6}
              
            />
            {fieldErrors.prompt && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.prompt}
              </Typography>
            )}

          </Box>

          <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end"
          }}>
            <Button
              variant="outlined"
              onClick={handleModalReset}
              disabled={loading || (!isEditMode && !isPromptChanged())}
              sx={{
                borderColor: "#E1AD01",
                color: "#E1AD01",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#B8941F",
                  backgroundColor: "rgba(212, 175, 55, 0.1)"
                }
              }}
            >
              {isEditMode ? "Cancel" : "Reset"}
            </Button>
            <Button
              variant="contained"
              onClick={handleModalCustomize}
              disabled={loading || (isEditMode && originalEditForm && JSON.stringify({ type: editForm.type || '', prompt: editForm.prompt || '' }) === JSON.stringify({ type: originalEditForm.type || '', prompt: originalEditForm.prompt || '' }))}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                position: "relative",
                "&:hover": {
                  backgroundColor: "#B8941F"
                }
                ,
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  backgroundColor: "#b8941fb3",
                  pointerEvents: "auto",
                  color: "#666666"
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#000" }} />
                  {isEditMode ? "Updating..." : "Adding..."}
                </Box>
              ) : (
                isEditMode ? "Update" : "Add"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "8px",
            maxWidth: "450px"
          }
        }}
      >
        <DialogContent sx={{ padding: "40px 32px 32px 32px", textAlign: "center" }}>
          {/* Delete Icon */}
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "#FFEBEE",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px"
            }}
          >
            <Delete sx={{ fontSize: 32, color: "#F44336" }} />
          </Box>

          <Typography sx={{
            fontSize: "24px",
            fontWeight: 600,
            color: "#333333",
            marginBottom: 2
          }}>Delete Compatibility?</Typography>

          <Typography sx={{
            fontSize: "16px",
            color: "#666666",
            marginBottom: 4,
            lineHeight: 1.5
          }}>
            Are you sure you want to delete this compatibility? This action is permanent.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={handleCancelDelete}
              disabled={actionLoading}
              sx={{
                borderColor: "#E0E0E0",
                color: "#666666",
                backgroundColor: "transparent",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                minWidth: "120px",
                "&:hover": {
                  borderColor: "#CCCCCC",
                  backgroundColor: "#F5F5F5"
                }
                ,
                "&.Mui-disabled": {
                  borderColor: "#E0E0E0",
                  color: "#999999",
                  cursor: "not-allowed",
                  pointerEvents: "auto"
                }
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={actionLoading}
              sx={{
                backgroundColor: "#F44336",
                color: "#FFFFFF",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: "16px",
                minWidth: "120px",
                "&:hover": { backgroundColor: "#D32F2F" },
                "&:disabled": {
                  backgroundColor: "#FFCDD2",
                  color: "#FFFFFF"
                }
              }}
            >
              {actionLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#FFF" }} />
                  Deleting...
                </Box>
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CompatibilityAI;